import apiClient from "./apiClient";
import { API_ORIGIN } from "../config/api";
import { AuditLogType, AuditPagination, AuditFilters } from "../types/audit";
export type AuditLog = AuditLogType;
export type AuditMeta = AuditPagination;
export type AuditScope = "me" | "all" | "actor" | "resource";
interface AuditResponse extends AuditPagination { data: AuditLog[] | null; }
const AUDIT_BASE = `${API_ORIGIN}/api/v1/audit-logs`;
function paramsFor(page: number, filters: AuditFilters, global: boolean) {
  const params = new URLSearchParams({ page: String(page), limit: "20" });
  if (filters.search.trim()) params.set("search", filters.search.trim());
  if (global && filters.actorId.trim()) params.set("actorId", filters.actorId.trim());
  if (global && filters.actorType) params.set("actorType", filters.actorType);
  // Comma-separated arrays also survive HTTP parameter-pollution middleware.
  if (filters.action.length) params.set("action", filters.action.join(","));
  if (filters.resource.length) params.set("resource", filters.resource.join(","));
  if (filters.resourceId?.trim()) params.set("resourceId", filters.resourceId.trim());
  if (filters.startDate) params.set("startDate", filters.startDate);
  if (filters.endDate) params.set("endDate", filters.endDate);
  if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) throw new Error("The end date must be on or after the start date.");
  return params;
}
export async function getAuditLogs(scope: AuditScope, page: number, filters: AuditFilters, signal?: AbortSignal) {
  const params = paramsFor(page, filters, scope !== "me");
  if (scope === "actor" && !filters.actorId.trim()) throw new Error("Enter an actor ID to view their history.");
  if (scope === "resource" && (!filters.resource.length || !filters.resourceId?.trim())) throw new Error("Select a resource and enter its ID to view its timeline.");
  const suffix = scope === "me" ? "/me" : scope === "actor" ? `/actor/${encodeURIComponent(filters.actorId.trim())}` : scope === "resource" ? "/resource-timeline" : "";
  const { data } = await apiClient.get<AuditResponse | null>(`${AUDIT_BASE}${suffix}`, { params, signal, timeout: 20000 });
  return { audits: data?.data ?? [], meta: data ? { total: data.total, totalPages: data.totalPages, page: data.page, limit: data.limit } : null };
}
export function getMyAuditLogs(page: number, filters: AuditFilters, signal?: AbortSignal) {
  return getAuditLogs("me", page, filters, signal);
}
export async function getAuditById(id: string, signal?: AbortSignal): Promise<AuditLog> {
  return (await apiClient.get<{ data: AuditLog }>(`${AUDIT_BASE}/${encodeURIComponent(id)}`, { signal })).data.data;
}
